import React, { ReactElement, useEffect, useState } from 'react';
import Buttons from '@components/Buttons';
import useNoodleApi from '@hooks/useNoodleApi';
import { updateWorkflow } from '@tsClient';
import Spacer from '@components/Spacer';
import FieldLabel from '@components/FormFields/FieldLabel';
import TextInput from '@components/FormFields/TextInput';
import {
  StepType,
  WORKFLOW_ACTIONS,
  WORKFLOW_TRIGGERS,
} from '@typings/api-models';
import TriggerCard from '@components/TriggerCard';
import ActionCard from '@components/ActionCard';
import { FullWorkflow, ThisWorkflowStep } from '@components/WorkflowCard/types';
import { PlusCircle } from '@phosphor-icons/react';
import updateWorkflowStep from '@tsClient/workflows/updateWorkflowStep';
import createWorkflowStep from '@tsClient/workflows/createWorkflowStep';
import removeNullish from '@helpers/removeNullish';
import { SendMessageActionData } from '@components/CreateEditSendMessageActionForm/CreateEditSendMessageActionForm';
import ProgressIndicator from '@components/ProgressIndicator';
import s from './EditWorkflowForm.module.scss';
import getAvailableTriggers from './getAvailableTriggers';

type Props = {
  workflow: FullWorkflow;
  initialMessage?: string | null;
  onSave: () => void;
  onEditAction: (action: ThisWorkflowStep) => void;
  refetchWorkflow: () => Promise<void>;
};

const EditWorkflowForm = ({
  workflow: initialWorkflow,
  refetchWorkflow,
  onEditAction,
  onSave,
}: Props): ReactElement => {
  const [workflow, setWorkflow] = useState(initialWorkflow);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddingSteps, setAddingSteps] = useState(false);

  const { getData: createStepFn } = useNoodleApi(createWorkflowStep);
  const { getData: updateWorkflowStepFn } = useNoodleApi(updateWorkflowStep, { toastOnError: true });
  const { getData: updateWorkflowFn } = useNoodleApi(updateWorkflow, { toastOnError: true });

  useEffect(() => {
    setWorkflow(initialWorkflow);
  }, [initialWorkflow]);

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    await updateWorkflowFn({
      id: workflow.id,
      updates: {
        name: workflow.name,
      },
    });
    onSave();
  };

  const updateStep = async ({ stepId, updates }: { stepId: string; updates: Partial<ThisWorkflowStep> }): Promise<void> => {
    setWorkflow({
      ...workflow,
      steps: workflow.steps.map((step) => step.id === stepId ? { ...step, ...updates } : step),
    });
    await updateWorkflowStepFn({
      id: workflow.id,
      stepId,
      updates,
    });
  };

  const addSteps = async (): Promise<void> => {
    setAddingSteps(true);
    const { data: newTrigger } = await createStepFn({
      id: workflow.id,
      stepData: {
        data: {},
        dependentOn: [workflow.steps[workflow.steps.length - 1] || null].filter(removeNullish),
        eventName: WORKFLOW_TRIGGERS.PRODUCT_PURCHASED,
        type: StepType.TRIGGER,
      },
    });
    if (newTrigger) {
      await createStepFn({
        id: workflow.id,
        stepData: {
          data: {},
          dependentOn: [newTrigger],
          eventName: WORKFLOW_ACTIONS.SEND_MESSAGE,
          type: StepType.ACTION,
        },
      });
    }
    await refetchWorkflow();
    setAddingSteps(false);
  };

  return (
    <div>
      <div className={s.editWorkflowForm}>
        <FieldLabel id="description">
          Workflow name
          <Spacer size={8}/>
          <TextInput
            placeholder={'Add workflow name'}
            id="name"
            value={workflow.name}
            onChange={(newName) => setWorkflow(prev => ({ ...prev, name: newName }))}
            hasFixedHeight={false}
          />
        </FieldLabel>
        {workflow.steps.map((step, idx) => (
          <div key={step.id} className={s.step}>
            {step.type === StepType.TRIGGER
              ? <TriggerCard
                availableTriggers={getAvailableTriggers({ step, workflow })}
                trigger={step}
                updateStep={updateStep}
              />
              : <ActionCard
                action={step}
                onEditClick={() => onEditAction(step)}
                actionData={step.data as SendMessageActionData}
              />}
            {step.type === StepType.TRIGGER && <div className={s.connector} />}
            {step.type === StepType.ACTION && idx !== workflow.steps.length - 1 && <Spacer size={16} />}
          </div>
        ))}
        <Spacer size='16px' />
        <Buttons isWrapper onClick={addSteps}>
          <div className={s.sendMessage__button}>
            {isAddingSteps ? <ProgressIndicator size={24} /> : <PlusCircle fill="var(--color-primary)" size={24} weight="fill" />}
            <strong className="body-sm-bold">Add Step</strong>
          </div>
        </Buttons>
        <Spacer />
        <Buttons
          isFetching={isSaving}
          isFullWidth
          isSecondary
          onClick={handleSave}
        >
        Save
        </Buttons>
      </div>
    </div>
  );
};

export default EditWorkflowForm;
